import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Device } from "@capacitor/core";
import { environment } from '@environment';
import { generarLog, mensajesSalida } from '../../constants/log-wfi.constants';
import { DestinoLogs, MdbCollections, TipoUsuario } from '../../enums/common.enum';
import { GlosaLogKeys, IDataAsesor, IDataCliente, IDataLog, IEntrada, IInputMapLog, IOperadorTypes, IValueLog, MensajesSalidaKeys } from '../../interfaces/logger.interface';
import { IDataToken } from '../../interfaces/shared.interface';
import * as moment from 'moment';
import * as publicIp from "public-ip";
import jwt_decode from 'jwt-decode';
import * as CryptoJS from 'crypto-js';
import { limpiarObjeto, setNombreCliente } from '../../helpers/utils';

@Injectable({
  providedIn: 'root',
})
export class LoggerService {
  constructor(private http: HttpClient) { }

  private saveLog(data: IInputMapLog) {
    return new Promise(async (resolve, reject) => {
      const endpoint = environment.ENDPOINTS.MS.MS_LOGGER + '/logger'
      this.http
        .post(endpoint, data)
        .toPromise()
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject({
            message: "Ha ocurrido un error al conectar con el servicio logs de  SURA INVESTMENTS",
            error,
          });
        });
    });
  }


  async generarLog(keyLog: GlosaLogKeys, keyMensajeSalida: MensajesSalidaKeys, detalleLog: any): Promise<void> {
    try {
      const token = sessionStorage.getItem("token") as string;

      if (!token) return;

      const tokenData: IDataToken = jwt_decode(token);

      if (!tokenData) return;

      const { rutEjecutivoAsesor = '', RutCliente = '' } = tokenData;
      const dataLog: IDataLog = generarLog(RutCliente, rutEjecutivoAsesor)[keyLog];
      dataLog.detalleLog = limpiarObjeto(detalleLog);
      dataLog.salida = mensajesSalida(RutCliente, rutEjecutivoAsesor)[keyMensajeSalida];

      const rut = this.esEjecutivo(tokenData) ? rutEjecutivoAsesor : RutCliente;
      const valueLog: IValueLog = await this.mapValueLog(dataLog, tokenData);
      const inputMapLog: IInputMapLog = {
        rut,
        valueLog: this.encriptarData(valueLog),
      };

      await this.saveLog(inputMapLog);
    } catch (error) {
      console.error('HA OCURRIDO UN ERROR en el metodo generarLog()', error);
    }
  }

  private async mapValueLog(dataLog: IDataLog, tokenData: IDataToken): Promise<IValueLog> {
    try {
      const entrada: IEntrada | string = await this.mapearDatosEntrada(dataLog, tokenData);
      const baseLog = this.crearBaseLog(dataLog);
      return {
        accion: dataLog.accion,
        glosa: dataLog.glosa,
        entrada,
        ...baseLog
      };
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  private async mapearDatosEntrada(dataLog: IDataLog, tokenData: IDataToken): Promise<IEntrada | string> {
    try {
      const [{ operatingSystem }, { agent, browserName }] = await this.getInfoDispositivoNavegador();
      const hostIP = await this.getIp();

      if (!tokenData) return '-';

      const esAsesor = this.esEjecutivo(tokenData);

      return {
        dataCliente: this.mapDatosCliente(tokenData),
        dataAsesor: this.mapDatosAsesor(tokenData, esAsesor),
        detalleLog: JSON.stringify(dataLog?.detalleLog ?? '-'),
        sessionCliente: sessionStorage.getItem("sessionId") || '',
        fecha: this.getFechaActual(null, 'dd/MM/yyyy HH:mm:ss').strFecha,
        hostIP,
        tipoPersona: this.getTipoPersona(tokenData?.TipoUsuario, esAsesor),
        canal: esAsesor ? 'NWSA' : 'NWS',
        tipoDispositivo: operatingSystem?.toUpperCase() || '-',
        modeloDispositivo: agent?.toUpperCase() || '-',
        navegador: browserName?.toUpperCase() || '-',
        tipoSesion: esAsesor ? 'ASESOR' : 'CLIENTE'
      };
    } catch (error) {
      console.error(error, 'Error al mapear datos de entrada');
      return '-';
    }
  }

  private mapDatosAsesor(tokenData: any, esAsesor: boolean): IDataAsesor | string {
    if (!esAsesor || !tokenData) return '-';

    const { rutEjecutivoAsesor = '-', cuentaAD = '-', correo = '-' } = tokenData;

    return {
      esAsesor,
      usuarioAsesor: cuentaAD,
      rutAsesor: rutEjecutivoAsesor,
      correoAsesor: correo,
    };
  }

  private mapDatosCliente(tokenData: any): IDataCliente | null {
    if (!tokenData) return null;

    const { RutCliente = '-', tipoIdentificacion = 'CI' } = tokenData;
    return {
      nombre: setNombreCliente(tokenData) ?? '-',
      rutCliente: RutCliente,
      tipoIdentificacion,
    };
  }

  private getTipoPersona(tipoUsuario: string, esAsesor: boolean): string {
    if (esAsesor) return '-';

    return tipoUsuario === 'EMPRESA' ? 'PERSONA JURIDICA' : 'PERSONA NATURAL';
  }

  private async getInfoDispositivoNavegador() {
    try {
      return await Promise.all([Device.getInfo(), this.getBrowserDetails()]);
    } catch (error) {
      console.log(error);
      return [{ operatingSystem: '-' }, { agent: '-', browserName: '-' }];
    }
  }

  private crearBaseLog(dataLog: IDataLog) {
    return {
      tipoLog: dataLog?.tipoLog ?? 'NEGOCIO',
      estado: dataLog?.estado ?? 'OK',
      metodo: dataLog?.metodo ?? 'LOG',
      salida: dataLog?.salida ?? '-',
      api: dataLog?.api ?? '-',
      destino: dataLog?.destino ?? DestinoLogs.MDB,
      repo: dataLog?.repo ?? MdbCollections.INVERSION_FONDOS_MUTUOS,
    };
  }

  esEjecutivo(tokenData: IDataToken) {
    try {
      return tokenData.TipoUsuario.toUpperCase() === TipoUsuario.EJECUTIVO;
    } catch (error) {
      return true;
    }
  }

  async getIp(): Promise<string> {
    try {
      return publicIp.v4();
    } catch (error) {
      return '-';
    }
  }

  getFechaActual(fechaInput = null, formato: string = 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'', timeZone = '') {
    timeZone = timeZone ? timeZone : environment.TIMEZONE;
    const formatoFecha = formato ?? 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'';
    const fechaUtc = !fechaInput ? new Date() : new Date(fechaInput);
    const fecha = moment(fechaUtc).tz(environment.TIMEZONE);

    return {
      strFecha: moment(fecha).tz(environment.TIMEZONE).format(formatoFecha),
      instanciaFecha: fecha,
      timestamp: Number(moment(fecha).tz(environment.TIMEZONE).format('T'))
    };
  }

  public getBrowserDetails(): any {
    try {
      const _window = window as any;
      const browser = {
        agent: window.navigator.userAgent.toLowerCase(),
        browserName: "other",
        version: "",
      };

      switch (true) {
        case browser.agent.indexOf("edg") > -1:
          browser.browserName = "edge";
          break;
        case browser.agent.indexOf("opr") > -1 && !!_window.opr:
          browser.browserName = "opera";
          break;
        case browser.agent.indexOf("chrome") > -1 && !!_window.chrome:
          browser.browserName = "chrome";
          break;
        case browser.agent.indexOf("trident") > -1:
          browser.browserName = "ie";
          break;
        case browser.agent.indexOf("firefox") > -1:
          browser.browserName = "firefox";
          break;
        case browser.agent.indexOf("safari") > -1:
          browser.browserName = "safari";
          break;
      }

      const verBrow = browser.agent.split(" ").filter((item: any) => {
        if (item.includes(browser.browserName) || item.includes("edg")) {
          return item;
        }
      });

      browser.version = verBrow[0].split("/")[1];
      return browser;
    } catch (err) {
      return {
        agent: "other",
        browserName: "other",
        version: "0",
      };
    }
  }

  encriptarData(data: any) {
      try {
        const jsonData = JSON.stringify(data);
        const dataToken = jwt_decode(sessionStorage.getItem("token")) as any;
        const key = dataToken.sesion;
        return CryptoJS.AES.encrypt(jsonData, key).toString();
      } catch (error) {
        console.log(error);
      }
  }
}
